import { FaceCullingMode, RenderLayer, } from '@skand/three-js-renderer';
import { Color, Quaternion, SphereGeometry, Vector2, Vector3 } from 'three';
import { ViewerError, ViewerErrorCode } from '../../../Utils';
const BALL_VERTICES = [];
const BALL_INDICES = [];
const sphere = new SphereGeometry(0.5);
const positions = sphere.getAttribute('position');
for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);
    BALL_VERTICES.push(new Vector3(x, y, z));
}
if (sphere.index) {
    for (let i = 0; i < sphere.index.array.length; i++) {
        BALL_INDICES.push(sphere.index.array[i]);
    }
}
/**
 * Create the panoramic sphere batchable.
 *
 * @returns
 */
function createSphereBatchable() {
    return {
        type: 'polygon',
        vertices: BALL_VERTICES,
        indices: BALL_INDICES,
        position: new Vector3(),
        rotation: new Quaternion(),
        scale: new Vector3(1, 1, 1),
        colors: [new Color(1, 1, 1)],
        material: {
            type: 'basicAlpha',
            color: new Color(1, 1, 1),
            alpha: 0.5,
            wireframe: false,
            faceCulling: FaceCullingMode.FrontFace,
        },
        renderLayer: RenderLayer.AfterPanorama,
    };
}
/**
 * 360 panoramic image.
 */
export class Panorama {
    /**
     * Create a new panorama model.
     *
     * @param name
     * @param url
     * @param thumbnailUrl
     * @param object
     * @param labelManager
     */
    constructor(name, url, thumbnailUrl, meshInstance, objectFactory, world, labelManager) {
        Object.defineProperty(this, "batchable", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "url", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "thumbnailUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "meshInstance", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "world", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "objectFactory", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "object360", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "labelManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "label", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "visible", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "visible360", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "opacity360", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.batchable = createSphereBatchable();
        this.url = url;
        this.thumbnailUrl = thumbnailUrl;
        this.name = name;
        this.meshInstance = meshInstance;
        this.objectFactory = objectFactory;
        this.world = world;
        this.object360 = null;
        this.labelManager = labelManager;
        this.label = null;
        this.visible = false;
        this.visible360 = false;
        this.opacity360 = 1.0;
    }
    async show() {
        this.visible = true;
        this.meshInstance.addMesh(this.batchable);
        return this;
    }
    async hide() {
        this.visible = false;
        this.meshInstance.removeMesh(this.batchable);
        return this;
    }
    async destroy() {
        await this.hide360();
        await this.hide();
    }
    isVisible() {
        return this.visible;
    }
    isPicked(pickResult) {
        const pickingId = this.meshInstance.getPickingId(this.batchable);
        const instance = this.meshInstance.getInstanceObject(this.batchable);
        return pickResult.object === instance && pickResult.userData === pickingId;
    }
    getBoundingRadius() {
        return Math.max(...this.getScale().toArray());
    }
    getPosition(position) {
        if (!position)
            position = new Vector3();
        position.copy(this.batchable.position);
        return position;
    }
    getRotation(rotation) {
        if (!rotation)
            rotation = new Quaternion();
        rotation.copy(this.batchable.rotation);
        return rotation;
    }
    getScale(scale) {
        if (!scale)
            scale = new Vector3(1, 1, 1);
        scale.copy(this.batchable.scale);
        return scale;
    }
    setPosition(position) {
        this.batchable.position.copy(position);
        this.meshInstance.refresh(this.batchable);
    }
    setRotation(rotation) {
        this.batchable.rotation.copy(rotation);
        this.object360?.setRotation(rotation);
        this.meshInstance.refresh(this.batchable);
    }
    setScale(scale) {
        this.batchable.scale.copy(scale);
        this.meshInstance.refresh(this.batchable);
    }
    /**
     * Get the name of the panorama.
     *
     * @returns
     */
    getName() {
        return this.name;
    }
    /**
     * Show the name label.
     */
    showName() {
        if (this.label === null) {
            this.label = this.labelManager.add(this.name, this.getPosition(), true, new Vector2(0, -20));
        }
    }
    /**
     * Hide the name label.
     */
    hideName() {
        if (this.label != null) {
            this.labelManager.remove(this.label);
            this.label = null;
        }
    }
    /**
     * Set the image URL to change the panorama image.
     *
     * Note: This will only take effect if the 360 sphere has not been loaded yet.
     *
     * @param url
     */
    setUrl(url) {
        this.url = url;
    }
    /**
     * Set the image URL to change the panorama thumbnail image.
     *
     * Note: This will only take effect if the 360 sphere has not been loaded yet.
     *
     * @param thumbnailUrl
     */
    setThumbnailUrl(thumbnailUrl) {
        this.thumbnailUrl = thumbnailUrl;
    }
    /**
     * Show the 360 sphere.
     */
    async show360() {
        if (this.object360 === null) {
            try {
                this.object360 = await this.objectFactory.createPanorama({ url: this.url });
            }
            catch (error) {
                throw new ViewerError(ViewerErrorCode.FailedLoadPanorama, error.message);
            }
            this.world.addPanorama(this.object360);
        }
        this.visible360 = true;
        this.object360.setVisible(this.visible360);
        this.object360.setOpacity(this.opacity360);
        this.object360.setRotation(this.getRotation());
        return this;
    }
    /**
     * Hide the 360 sphere.
     */
    async hide360() {
        this.visible360 = false;
        this.object360?.setVisible(this.visible360);
        return this;
    }
    /**
     * Set the opacity of the 360 sphere.
     *
     * @param opacity
     */
    setOpacity360(opacity) {
        this.opacity360 = opacity;
        this.object360?.setOpacity(opacity);
    }
    /**
     * Get the opacity of the 360 sphere.
     *
     * @returns
     */
    getOpacity360() {
        return this.opacity360;
    }
    /**
     * Set the render priority of the panoramic sphere
     *
     * @param rank
     */
    setPriority(rank) {
        this.object360?.setPriority(rank);
    }
    /**
     * Check if the name label is visible.
     *
     * @returns
     */
    isNameVisible() {
        return this.label !== null;
    }
    /**
     * Set the color of the widget.
     *
     * @param color
     */
    setColor(color) {
        this.batchable.colors[0].copy(color);
        this.meshInstance.refresh(this.batchable);
    }
}
